import { useState, useEffect } from "react";
import { MessageSquare, Send, User } from "lucide-react";
import { Card, CardContent, CardHeader, CardTitle } from "../../components/ui/card";
import { Button } from "../../components/ui/button";
import { Textarea } from "../../components/ui/textarea";
import { toast } from "sonner";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const CustomerMessages = ({ customer }) => {
  const [messages, setMessages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [newMessage, setNewMessage] = useState("");
  const [sending, setSending] = useState(false);

  useEffect(() => {
    fetchMessages();
  }, []);

  const fetchMessages = async () => {
    try {
      const response = await fetch(`${API}/customer/messages`, { credentials: "include" });
      if (response.ok) {
        const data = await response.json();
        setMessages(data?.messages ?? data ?? []);
      } else {
        setMessages([]);
      }
    } catch (error) {
      toast.error("Failed to load messages");
      setMessages([]);
    } finally {
      setLoading(false);
    }
  };

  const handleSend = async () => {
    if (!newMessage.trim()) return;

    setSending(true);
    try {
      const response = await fetch(`${API}/customer/messages`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        credentials: "include",
        body: JSON.stringify({ message: newMessage })
      });

      if (response.ok) {
        toast.success("Message sent");
        setNewMessage("");
        fetchMessages();
      }
    } catch (error) {
      toast.error("Failed to send message");
    } finally {
      setSending(false);
    }
  };

  const formatTime = (dateStr) => {
    const date = new Date(dateStr);
    return date.toLocaleString('en-US', {
      month: 'short',
      day: 'numeric',
      hour: 'numeric',
      minute: '2-digit',
      hour12: true
    });
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="w-12 h-12 border-4 border-[#D4AF37] border-t-transparent rounded-full animate-spin"></div>
      </div>
    );
  }

  return (
    <div className="space-y-6 animate-fade-in" data-testid="customer-messages">
      <div>
        <h1 className="text-2xl md:text-3xl font-bold text-[#FAFAFA]">Messages</h1>
        <p className="text-[#A1A1AA] mt-1">Communicate with the DopeInHD team</p>
      </div>

      <Card className="bg-[#121214] border-[#27272A]">
        <CardContent className="pt-6">
          {/* Message Input */}
          <div className="mb-6">
            <Textarea
              value={newMessage}
              onChange={(e) => setNewMessage(e.target.value)}
              placeholder="Type your message or question..."
              className="bg-[#1A1A1D] border-[#27272A] text-[#FAFAFA] min-h-[100px]"
              data-testid="message-input"
            />
            <div className="flex justify-end mt-3">
              <Button 
                onClick={handleSend} 
                disabled={!newMessage.trim() || sending}
                className="btn-gold"
                data-testid="send-message-btn"
              >
                {sending ? (
                  <div className="w-4 h-4 border-2 border-[#0A0A0B] border-t-transparent rounded-full animate-spin mr-2"></div>
                ) : (
                  <Send size={16} className="mr-2" />
                )}
                Send Message
              </Button>
            </div>
          </div>

          {/* Messages List */}
          <div className="space-y-4">
            {(messages || []).map((msg) => (
              <div 
                key={msg.id}
                className={`p-4 rounded-lg ${
                  msg.sender_type === 'customer' 
                    ? 'bg-[#D4AF37]/10 ml-8' 
                    : 'bg-[#1A1A1D] mr-8'
                }`}
                data-testid={`message-${msg.id}`}
              >
                <div className="flex items-center gap-2 mb-2">
                  <div className={`w-6 h-6 rounded-full flex items-center justify-center text-xs ${
                    msg.sender_type === 'customer' 
                      ? 'bg-[#D4AF37] text-[#0A0A0B]' 
                      : 'bg-[#3B82F6] text-white'
                  }`}>
                    {msg.sender_name?.charAt(0)}
                  </div>
                  <span className="text-sm font-medium text-[#FAFAFA]">{msg.sender_name}</span>
                  <span className="text-xs text-[#71717A]">{formatTime(msg.created_at)}</span>
                </div>
                <p className="text-[#FAFAFA]">{msg.message}</p>
              </div>
            ))}

            {(messages || []).length === 0 && (
              <div className="text-center py-12">
                <MessageSquare size={48} className="text-[#71717A] mx-auto mb-3" />
                <p className="text-[#FAFAFA] font-medium">No Messages Yet</p>
                <p className="text-[#71717A]">Send a message to start the conversation</p>
              </div>
            )}
          </div>
        </CardContent>
      </Card>
    </div>
  );
};

export default CustomerMessages;
